import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { IChatbotConfigurationProps } from "./types/configAgentProps"

import { MixpanelProperties } from "src/analytics/constants/properties"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import {
    avy_api_pkg_segment_SegmentType,
    ChatbotAdminService,
    config_ConfigDefinition,
    config_DynamicConfigMap,
} from "src/api"
import { loads } from "src/channel/utils"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { FormFields } from "src/lib/form-fields"
import { createLoadingKeys } from "src/lib/loading"
import { Channel } from "src/channel"
import { reportError } from "src/lib/report"

import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"
import { ConfigMode } from "src/types/dynamic-configurable-form-fields/config-mode"
import { IHandleSubmitConfigurableDynamicFieldsProps } from "src/types/dynamic-configurable-form-fields/handle-submit-props"
import { removeEmptyProperties } from "src/helpers/removeEmptyProperties"

export class ChatbotConfigurationStore {
    static Context = React.createContext<ChatbotConfigurationStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "submit")

    isLoading = false
    form = new FormFields<IChatbotConfigurationProps>({
        access_group_id: DEFAULT_ACCESS_GROUP.id,
        access_type: "NONE",
        feature_config_id: undefined,
        name: "",
        segment_ids: [],
        configs: {},
        config_type: [],
    })
    private id: string | null = null
    private _accessType: string = "WRITE"
    private _segmentIds: Array<number> = []

    constructor() {
        makeAutoObservable(this)
    }

    private _accessGroupId: number = DEFAULT_ACCESS_GROUP.id

    get accessGroupId() {
        return this._accessGroupId
    }

    private _configDefinition: config_ConfigDefinition = {}

    get configDefinition() {
        return this._configDefinition
    }

    private _configTypeOptions: IGenericAutoCompleteTextFieldValue[] = []

    get configTypeOptions() {
        return this._configTypeOptions
    }

    private _publishingLevel?: avy_api_pkg_segment_SegmentType = undefined

    get publishingLevel(): avy_api_pkg_segment_SegmentType | undefined {
        return this._publishingLevel
    }

    private _configMode: ConfigMode = ConfigMode.Single

    get configMode() {
        return this._configMode
    }

    //#region getters
    get segmentIDs() {
        return this._segmentIds
    }

    get isReadOnly() {
        return this._accessType === "READ"
    }

    get isEditMode() {
        return this.id !== null
    }

    get configTypeValue() {
        const configType = this.form.get("config_type") ?? []

        if (this._configMode === ConfigMode.Multiple) {
            return configType
        }

        return configType.length > 0 ? configType[0] : null
    }

    setConfigType(
        value:
            | IGenericAutoCompleteTextFieldValue
            | IGenericAutoCompleteTextFieldValue[]
            | null,
    ) {
        if (value === null) {
            this.form.set("config_type", [])
            return
        }

        const safeValue = Array.isArray(value) ? value : [value]
        this.form.set("config_type", safeValue)

        this.setPublishingLevel(
            this.getSelectedConfigTypeConfigurationData()?.publishing_level,
        )
    }
    //#endregion

    //#region setters
    setAccessGroupId(id: number) {
        this._accessGroupId = id
    }

    setSegmentIDs(segmentIDs: number[]) {
        this.form.set("segment_ids", segmentIDs)
        this._segmentIds = segmentIDs
    }

    setConfigMode(value?: ConfigMode) {
        this._configMode = value ?? ConfigMode.Single
    }

    setPublishingLevel(level?: avy_api_pkg_segment_SegmentType) {
        this._publishingLevel = level
    }

    setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)

    //#region operations
    @loads(() => ChatbotConfigurationStore.LoadingKeys.init)
    async init(id: string | undefined, accessGroupId: number) {
        try {
            this.setAccessGroupId(accessGroupId)

            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {},
                )
            this.setConfigDefinition(configDefinition)
            this.setConfigTypeOptions(
                configDefinition.definition?.map((field) => ({
                    id: field.name ?? "",
                    label: field.name ?? "",
                })) ?? [],
            )
            this.setConfigMode(configDefinition.config_mode as ConfigMode)

            if (id !== undefined) {
                this.setId(id)

                const response =
                    await ChatbotAdminService.getV1AdminChatbotAgentConfig({
                        configId: id,
                    })

                this.form.init({
                    access_group_id:
                        response?.access_group_id ?? DEFAULT_ACCESS_GROUP.id,
                    access_type: response?.access_type ?? "NONE",
                    feature_config_id: response?.feature_config_id,
                    name: response?.name ?? "",
                    segment_ids: response?.segment_ids ?? [],
                    configs: response?.config ?? {},
                    config_type:
                        response?.config !== undefined
                            ? Object.keys(response.config).map((name) => ({
                                  id: name,
                                  label: name,
                              }))
                            : [],
                })

                const selectedConfigTypeConfigurationData =
                    this.getSelectedConfigTypeConfigurationData()
                this.setPublishingLevel(
                    selectedConfigTypeConfigurationData?.publishing_level,
                )

                if (response?.segment_ids !== undefined) {
                    this.setSegmentIDs(response.segment_ids)
                }
                this.setAccessGroupId(
                    response?.access_group_id ?? DEFAULT_ACCESS_GROUP.id,
                )
                if (response?.access_type !== undefined) {
                    this.setAccessType(response?.access_type)
                }
            }
        } catch (error) {
            reportError(t`chatbot-configuration-detail.load-data-fail`, error)
            this.setAccessType("READ")
        }
    }

    async update(id: string) {
        const { data } = this.form

        let config = data.config_type.reduce((acc, configType) => {
            acc[configType.id] = data.configs[configType.id] ?? {}
            return acc
        }, {} as config_DynamicConfigMap)

        const cleanedConfig = removeEmptyProperties(config)

        await ChatbotAdminService.putV1AdminChatbotAgentConfig({
            configId: id,
            request: {
                name: data.name,
                access_group_id: data.access_group_id,
                config: cleanedConfig,
            },
        })

        await ChatbotAdminService.putV1AdminChatbotAgentConfigPublish({
            configId: id,
            request: {
                published_in: this._segmentIds,
            },
        })

        Channel.send({
            name: "repository/updated",
            payload: {
                repository: "chatbot-configuration",
                action: "update",
            },
        })
    }

    async create() {
        const { data } = this.form

        let config = data.config_type.reduce((acc, configType) => {
            acc[configType.id] = data.configs[configType.id] ?? {}
            return acc
        }, {} as config_DynamicConfigMap)

        const cleanedConfig = removeEmptyProperties(config)

        const response =
            await ChatbotAdminService.postV1AdminChatbotAgentConfig({
                request: {
                    name: data.name,
                    access_group_id: this.accessGroupId,
                    config: cleanedConfig,
                },
            })

        if (response.feature_config_id !== undefined) {
            await ChatbotAdminService.putV1AdminChatbotAgentConfigPublish({
                configId: `${response.feature_config_id}`,
                request: {
                    published_in: this._segmentIds,
                },
            })
        }

        Channel.send({
            name: "repository/updated",
            payload: {
                repository: "chatbot-configuration",
                action: "create",
            },
        })
    }

    @loads(() => ChatbotConfigurationStore.LoadingKeys.submit)
    async submit() {
        const { id } = this
        try {
            await this.form.catchErrors(async () => {
                if (id !== null) {
                    await this.update(id)
                } else {
                    await this.create()
                }
            })
        } catch (error) {
            reportError(t`chatbot-configuration-detail.save-item-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }
    //#endregion

    async handleSubmit({
        defaultLanguage,
        validateConfigurableDynamicFields,
        getConfigsApiPayload,
    }: IHandleSubmitConfigurableDynamicFieldsProps) {
        this.form.validateRequiredFields([
            { field: "name" },
            {
                field: "config_type",
                validate: (val) =>
                    Array.isArray(val) ? Boolean(val.length) : false,
            },
        ])

        const configurableDynamicFieldsHasError =
            validateConfigurableDynamicFields(defaultLanguage) === false

        if (
            this.form.hasErrors() ||
            configurableDynamicFieldsHasError === true
        ) {
            this.setIsLoading(false)
            return false
        }

        this.form.set("configs", getConfigsApiPayload() ?? {})

        trackModuleEvent("Chatbot Agent Items | Save", {
            [MixpanelProperties.ItemName]: this.form.data.name,
            [MixpanelProperties.AccessGroupID]: this.form.data.access_group_id,
        })

        await this.submit()
        return true
    }

    //# region helpers
    getSelectedConfigTypeConfigurationData() {
        return this.configDefinition.definition?.find(
            (field) => field.name === this.form.get("config_type")?.[0]?.id,
        )
    }

    shouldShowPublishingSegments = (): boolean => {
        const configType = this.form.get("config_type") ?? []

        if (configType.length === 0) {
            return true // Show when no config type selected
        }

        // Check if the selected config type requires instances
        const selectedConfigData = this.getSelectedConfigTypeConfigurationData()

        if (selectedConfigData?.metadata != null) {
            const agentInstanceRequired = (
                selectedConfigData.metadata as Record<string, unknown>
            )?.agent_instance_required

            // Don't show publishing segments when agent_instance_required is true
            return !Boolean(agentInstanceRequired)
        }

        // Default to showing if metadata is not available (unknown case)
        return true
    }

    private setId(id: string) {
        this.id = id
    }

    private setAccessType(accessType: string) {
        this._accessType = accessType
    }

    private setConfigDefinition(fields: config_ConfigDefinition) {
        this._configDefinition = fields
    }
    //#endregion

    private setConfigTypeOptions(
        options: {
            id: string
            label: string
        }[],
    ) {
        this._configTypeOptions = options
    }
    //#endregion
}
